// Async AI-powered decision engine — calls the recommend edge function
// Post-processes results through category scoring, fraud, taxonomy, and buy/wait logic

import { supabase } from "@/integrations/supabase/client";
import type { ParsedQuery } from "./queryParser";
import type { DecisionOption, CoverageState, TrustProof, ConfidenceLevel } from "./decisionEngine";
import { classifyQuery, getQueryBehavior } from "./queryTaxonomy";
import { getCategoryScoring } from "./categoryScoring";
import { analyzeFraudSignals, getFraudRiskLevel } from "./fraudIntelligence";
import { getBuyAdvice, getBuyVerdictLabel } from "./buyWaitLogic";

// ─── Types ─────────────────────────────────────────────────────────────────

interface VerifiedOffer {
  retailer: string;
  price: string;
  priceUSD: number;
  url: string;
  trustScore: number;
  delivery: string;
  isVerified: boolean;
  isDomestic: boolean;
  landedCost: {
    productPrice: number;
    shippingCost: number;
    taxEstimate: number;
    dutyEstimate: number;
    totalLanded: number;
    currency: string;
    isDomestic: boolean;
    breakdown: string;
  };
  linkType?: "product-page" | "search-page" | "retailer-visit";
}

interface LandedCostData {
  productPrice: number;
  shippingCost: number;
  taxEstimate: number;
  dutyEstimate: number;
  totalLanded: number;
  currency: string;
  isDomestic: boolean;
  breakdown: string;
}

interface EnrichedProduct {
  name: string;
  priceUSD: number;
  price: string;
  delivery: string;
  label: "Recommended" | "Best Value" | "Premium Pick" | "Fastest Delivery";
  verdict: string;
  explanation: string;
  tradeoff: string;
  scores: { price: number; delivery: number; trust: number; quality: number };
  buyUrl?: string;
  buyLinkType?: "product-page" | "search-page" | "retailer-visit";
  landedCost?: LandedCostData;
  verifiedOffers: VerifiedOffer[];
  bestVerifiedPrice?: number;
  priceVerified: boolean;
  verification: "verified" | "partially-verified" | "ai-estimated";
  eliminationReasons?: string[];
}

interface AuditData {
  totalOffersChecked: number;
  totalSourcesQueried: number;
  offersFiltered: number;
  verifiedOffers: number;
  aiEstimatedOffers: number;
  marketsSearched: string[];
  retailersQueried: string[];
  scrapeDurationMs: number;
  filterReasons: string[];
  domainsBlocked?: number;
}

interface PipelineResult {
  products: EnrichedProduct[];
  confidence: "high" | "medium" | "low";
  audit: AuditData;
}

export type AIErrorType = "rate_limit" | "rate_limit_anonymous" | "upgrade_required" | "service_unavailable" | "generic_error" | "limit_reached";

// ─── Session Cache ─────────────────────────────────────────────────────────

function getCacheKey(query: string, budget?: number, market?: string, clarifications?: { question: string; answer: string }[]): string {
  const clarKey = clarifications?.map(c => `${c.question}:${c.answer}`).join("|") || "";
  return `dos-cache-${query.toLowerCase().trim()}-${budget ?? "any"}-${market ?? "US"}-${clarKey}`;
}

function getCachedResult(key: string): PipelineResult | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > 10 * 60 * 1000) { sessionStorage.removeItem(key); return null; }
    return data;
  } catch { return null; }
}

function setCachedResult(key: string, data: PipelineResult) {
  try { sessionStorage.setItem(key, JSON.stringify({ data, ts: Date.now() })); } catch {}
}

// ─── URL Validation ────────────────────────────────────────────────────────

function isValidBuyUrl(url: string): boolean {
  try {
    const u = new URL(url);
    return /^https?:$/.test(u.protocol) && u.hostname.includes(".");
  } catch { return false; }
}

// ─── Error Classification ──────────────────────────────────────────────────

function classifyError(error: string | undefined, data: any): AIErrorType {
  const errorType = data?.errorType;
  if (errorType) return errorType as AIErrorType;
  const msg = (error || "").toLowerCase();
  if (msg.includes("daily limit") || msg.includes("rate limit")) return "rate_limit";
  if (msg.includes("free account") || msg.includes("try again tomorrow")) return "rate_limit_anonymous";
  if (msg.includes("upgrade")) return "upgrade_required";
  if (msg.includes("credits exhausted") || msg.includes("service") || msg.includes("unavailable")) return "service_unavailable";
  return "generic_error";
}

// ─── Fetch ─────────────────────────────────────────────────────────────────

export async function fetchAIRecommendations(
  parsed: ParsedQuery,
  rawQuery: string,
  clarifications?: { question: string; answer: string }[],
  refineParams?: { optimizeFor?: string; localOnly?: boolean }
): Promise<{ recommendations: PipelineResult | null; error: string | null; errorType?: AIErrorType; usage?: { usage_count: number; limit: number; remaining: number; resetAt: string; plan: string }; intentKey?: string; marketSupported?: boolean; isTrialResult?: boolean; trialExhausted?: boolean; trialRemaining?: number }> {
  try {
    const cacheKey = getCacheKey(rawQuery, parsed.budget?.max, parsed.country || parsed.region, clarifications);
    // Don't use cache for refine requests
    if (!refineParams) {
      const cached = getCachedResult(cacheKey);
      if (cached) return { recommendations: cached, error: null };
    }

    // Get visitor ID for guest tracking
    let visitorId: string | undefined;
    try {
      visitorId = localStorage.getItem("dos_visitor_id") || undefined;
    } catch {}

    const headers: Record<string, string> = {};
    if (visitorId) headers["x-visitor-id"] = visitorId;

    const { data, error } = await supabase.functions.invoke("recommend", {
      body: {
        query: rawQuery,
        category: parsed.category || parsed.productType,
        budget: parsed.budget?.max,
        currency: parsed.budget?.currency || "USD",
        market: parsed.country || parsed.region || "US",
        priority: refineParams?.optimizeFor || parsed.priorities[0] || "",
        useCase: parsed.useCase || "",
        clarifications: clarifications || [],
        refine: refineParams || undefined,
      },
      headers,
    });

    if (error) {
      const errType = classifyError(error.message, data);
      return { recommendations: null, error: error.message, errorType: errType, usage: data?.usage || data };
    }

    if (data?.error) {
      const errType = classifyError(data.error, data);
      return { recommendations: null, error: data.error, errorType: errType, usage: data };
    }

    if (!data?.products || !Array.isArray(data.products) || data.products.length < 2) {
      return { recommendations: null, error: "Invalid response structure", errorType: "generic_error" };
    }

    // Extract usage, intentKey, and market support from response before caching
    const usage = data.usage;
    const intentKey = data.intentKey;
    const marketSupported = data.marketSupported !== false;
    const isTrialResult = data.isTrialResult === true;
    const trialExhausted = data.trialExhausted === true;
    const trialRemaining = typeof data.trialRemaining === "number" ? data.trialRemaining : undefined;
    const result = data as PipelineResult;
    setCachedResult(cacheKey, result);
    return { recommendations: result, error: null, usage, intentKey, marketSupported, isTrialResult, trialExhausted, trialRemaining };
  } catch (e) {
    return { recommendations: null, error: e instanceof Error ? e.message : "Unknown error", errorType: "generic_error" };
  }
}

// ─── Post-Processing ───────────────────────────────────────────────────────

function applyCategoryScoring(
  product: EnrichedProduct,
  category: string,
): { adjustedScores: EnrichedProduct["scores"]; categoryMeta: ReturnType<typeof getCategoryScoring> } {
  const catScoring = getCategoryScoring(category);
  const adjustedScores = {
    price: Math.max(0, Math.min(100, Math.round(product.scores.price * (catScoring.priceWeight / 0.25)))),
    delivery: Math.max(0, Math.min(100, Math.round(product.scores.delivery * (catScoring.deliveryWeight / 0.25)))),
    trust: Math.max(0, Math.min(100, Math.round(product.scores.trust * (catScoring.trustWeight / 0.25)))),
    quality: Math.max(0, Math.min(100, Math.round(product.scores.quality * (catScoring.qualityWeight / 0.25)))),
  };
  return { adjustedScores, categoryMeta: catScoring };
}

function applyBuyWaitLogic(product: EnrichedProduct, category: string, medianPrice: number, budget?: number) {
  const budgetFit = !budget ? "unknown" as const
    : product.priceUSD <= budget * 0.9 ? "under" as const
    : product.priceUSD <= budget * 1.1 ? "at" as const
    : "over" as const;

  return getBuyAdvice({
    category,
    currentPrice: product.priceUSD,
    medianPrice,
    priceDirection: "unknown",
    qualityScore: product.scores.quality,
    urgency: "none",
    budgetFit,
  });
}

// ─── Convert Pipeline Results to Decisions ─────────────────────────────────

export function aiResultToDecisions(
  pipeline: PipelineResult,
  parsed: ParsedQuery,
  coverage: CoverageState
): { decisions: DecisionOption[]; trustProof: TrustProof; queryBehavior: ReturnType<typeof getQueryBehavior> } {
  const tierMap: Record<string, "A" | "B" | "C"> = {
    Recommended: "A", "Best Value": "B", "Premium Pick": "C", "Fastest Delivery": "C",
  };

  const confidenceMap: Record<string, ConfidenceLevel> = {
    high: "high", medium: "medium", low: "low",
  };

  const queryClass = classifyQuery(parsed.raw || "");
  const queryBehavior = getQueryBehavior(queryClass);
  const category = parsed.category || parsed.productType || "general";
  const medianPrice = pipeline.products.reduce((sum, p) => sum + p.priceUSD, 0) / pipeline.products.length;

  const { adjustedScores: winnerScores } = applyCategoryScoring(pipeline.products[0], category);
  const winnerComposite = winnerScores.price * 0.22 + winnerScores.delivery * 0.18 + winnerScores.trust * 0.25 + winnerScores.quality * 0.25;
  const buyAdvice = applyBuyWaitLogic(pipeline.products[0], category, medianPrice, parsed.budget?.max);

  const audit = pipeline.audit;
  const hasRealData = audit.verifiedOffers > 0;

  // Determine effective coverage
  const effectiveCoverage: CoverageState = hasRealData ? "partial" : "demo";

  const decisions: DecisionOption[] = pipeline.products.map((p, i) => {
    const { adjustedScores } = applyCategoryScoring(p, category);
    const fraud = analyzeFraudSignals(
      { name: p.name, price: p.priceUSD, domain: p.buyUrl ? new URL(p.buyUrl).hostname : undefined },
      medianPrice,
    );
    const riskLevel = getFraudRiskLevel(fraud);

    const composite = adjustedScores.price * 0.22 + adjustedScores.delivery * 0.18 + adjustedScores.trust * 0.25 + adjustedScores.quality * 0.25;
    const dominanceGap = i === 0 ? 0 : Math.round(winnerComposite - composite);

    let effectiveConfidence = confidenceMap[pipeline.confidence] || "medium";
    if (riskLevel === "dangerous" || riskLevel === "risky") effectiveConfidence = "low";
    if (p.verification === "verified") effectiveConfidence = "high";

    const auditFilteredOut: string[] = [];
    if (fraud.length > 0) auditFilteredOut.push(...fraud.map(s => s.description));
    if (audit.filterReasons.length > 0) {
      auditFilteredOut.push(...audit.filterReasons.slice(0, 3));
    }

    let verdict = p.verdict;
    if (i === 0 && buyAdvice.verdict !== "buy_now") {
      verdict = `${getBuyVerdictLabel(buyAdvice.verdict)}. ${buyAdvice.reason}`;
    }

    // Use best verified offer URL if available
    const bestOffer = p.verifiedOffers.sort((a, b) => a.priceUSD - b.priceUSD)[0];
    const effectiveBuyUrl = bestOffer?.url || (p.buyUrl && isValidBuyUrl(p.buyUrl) ? p.buyUrl : undefined);

    // Build scope line
    const scopeParts: string[] = [];
    if (hasRealData) {
      scopeParts.push(`Checked ${audit.totalOffersChecked} offers across ${audit.totalSourcesQueried} sources`);
      if (audit.offersFiltered > 0) scopeParts.push(`Filtered ${audit.offersFiltered} weak options`);
    } else {
      scopeParts.push(`AI-analyzed for ${category} in ${parsed.country || parsed.region || "US"} market`);
    }

    // Build retrieval summary
    const retrievalParts: string[] = [];
    if (p.verification === "verified") retrievalParts.push("Price verified from multiple retailer sources.");
    else if (p.verification === "partially-verified") retrievalParts.push("Price confirmed from one retailer source.");
    else retrievalParts.push("AI recommendation only. No live retailer prices found for this product.");
    if (p.landedCost) retrievalParts.push(`Est. landed cost: ${p.landedCost.breakdown}`);
    if (riskLevel !== "safe") retrievalParts.push(`Risk level: ${riskLevel}.`);
    if (buyAdvice.verdict !== "buy_now") retrievalParts.push(`Timing: ${getBuyVerdictLabel(buyAdvice.verdict)}.`);

    return {
      id: `dec-${i + 1}`,
      label: p.label as DecisionOption["label"],
      tier: tierMap[p.label] || "B",
      productName: p.name,
      verdict,
      explanation: p.explanation,
      tradeoff: p.tradeoff,
      scores: adjustedScores,
      compositeScore: Math.round(composite * 10) / 10,
      dominanceGap,
      price: p.price,
      deliveryTime: p.delivery,
      buyUrl: effectiveBuyUrl,
      confidence: effectiveConfidence,
      audit: {
        whyWon: i === 0
          ? [`Strongest combined score across ${category}-weighted dimensions (trust×${getCategoryScoring(category).trustWeight}, quality×${getCategoryScoring(category).qualityWeight})`]
          : [p.tradeoff],
        whyOthersLost: i === 0
          ? pipeline.products.slice(1).map(o => `${o.name}: ${o.tradeoff}`)
          : [`Outscored by ${pipeline.products[0].name} on category-weighted metrics`],
        filteredOut: auditFilteredOut.length > 0 ? auditFilteredOut : ["No issues detected"],
        scope: scopeParts.join(". ") + ".",
        sourcesAnalyzed: audit.totalSourcesQueried,
        offersCompared: audit.totalOffersChecked,
        optionsFiltered: audit.offersFiltered,
        dataConsistency: p.verification === "verified" ? "high" : p.verification === "partially-verified" ? "medium" : "low",
        lastChecked: "Just now",
        retrievalSummary: retrievalParts.join(" "),
        eliminationReasons: p.eliminationReasons || [],
      },
      freshness: hasRealData ? "fresh" as const : "recent" as const,
      // Extended fields for UI
      verifiedOffers: p.verifiedOffers,
      landedCost: p.landedCost,
      verification: p.verification,
      priceVerified: p.priceVerified,
      bestVerifiedPrice: p.bestVerifiedPrice,
    };
  });

  const trustProof: TrustProof = {
    offersChecked: audit.totalOffersChecked,
    sourcesChecked: audit.totalSourcesQueried,
    removed: audit.offersFiltered,
    removalReasons: audit.filterReasons.length > 0
      ? audit.filterReasons.slice(0, 5)
      : ["No offers needed removal"],
    market: parsed.country || parsed.region || "US",
    candidatesRetrieved: audit.totalOffersChecked,
    retrievalModes: hasRealData ? ["firecrawl-verified", "ai-powered"] : ["ai-powered"],
  };

  return { decisions, trustProof, queryBehavior };
}
